import { create } from 'zustand';
import {
  CustomerState,
  CustomerStore,
  HouseholdUpdate,
} from '@/store/customer/types';
import { feldSpeicherFehler, toErrorType } from '@/utils';
import api from '@/services/api';
import { DocumentsTable, Insurance } from '@/types';
import { createJSONStorage, persist } from 'zustand/middleware';
import { LocalInsurance } from '@/views/home/customer/planner/SavingsIndividual';
import { generateUUID } from '@/utils/generateUUID';

// Customer-Felder, deren Änderung die Förder-Berechnung beeinflusst — bei
// Save wird der funding-Cache geleert, damit die Förder-Ansicht neu fetcht
// (z. B. use_riester treibt die backend-berechnete Riester-sum_total).
const FUNDING_RELEVANT_KEYS: readonly string[] = [
  'use_riester',
  'children',
  'child_benefit_eligible_children',
  'has_building_savings_contract',
  'employment_status',
  'gross_salary',
  'pays_church_tax',
  'capital_forming_benefits_allowance',
  'capital_forming_benefits_amount',
  'net_salary',
  'school_years_after_17',
  // Beide gehen in die Backend-Rechnung ein (Tilgung/Kapitalaufbau liest das
  // Alter, die Wohnungsbauprämie den Familienstand) — fehlten hier aber, die
  // Tabelle stimmte erst nach einem Reload (FK-Punkt 07.09.2026, P2-11).
  'date_of_birth',
  'marital_status',
  // Die Partner-Karte schreibt IMMER das komplette partner_data-Objekt (eine
  // JSONB-Spalte, kein Endpoint pro Key). Ohne diesen Eintrag blieb der
  // Foerder-Cache stehen, wenn beim Partner VWL, Riester, Schulzeit oder
  // Erwerbsstatus geaendert wurden — die Summe stimmte erst nach einem
  // Reload. Seit dem Partner-Resolver (#143) rechnen genau diese Werte mit,
  // also gehoert der Schluessel hierher.
  'partner_data',
];

// Stand des Förder-Caches. Jedes Speichern eines förderrelevanten Feldes zählt
// hoch; eine Abfrage übernimmt ihre Antwort nur, wenn während ihrer Laufzeit
// nichts gespeichert wurde. Sonst landete eine ALTE Antwort im Cache, und die
// Tabelle fragte nicht erneut — ihr Effekt auf `funding` feuert bei null → null
// nicht (FK-Punkt 07.09.2026, P2-11). Bewusst außerhalb des Stores: der Wert
// gehört nicht in den persistierten Zustand.
let foerderGeneration = 0;
let foerderAbfrage: {
  generation: number;
  customerId: number;
  promise: Promise<void>;
} | null = null;

/**
 * Nach dem Speichern eines förderrelevanten Feldes: Cache leeren, laufende
 * Abfragen entwerten und gezielt neu holen — nicht darauf warten, dass eine
 * Ansicht es bemerkt.
 */
const erneuereFoerderCache = (
  set: (teil: Partial<CustomerStore>) => void,
  getFunding: () => Promise<void>,
): void => {
  foerderGeneration += 1;
  set({ funding: null });
  getFunding().catch(() => {
    // getFunding legt Fehler selbst in `error` ab.
  });
};

// Haushaltsrechner (Feedback 15.10.2026: „Haushaltsrechner schon wieder
// gelöscht, mitten in der Beratung"). Das Backend ersetzt den Haushalt beim
// Speichern KOMPLETT (`HouseholdService::create`: delete() + create()) — jeder
// Speicherlauf auf einem falschen Stand ist ein Loeschlauf.
//
// Speicherlaeufe laufen deshalb NACHEINANDER: jeder Blur schickt eine
// vollstaendige Momentaufnahme, und parallel konnte die Antwort eines
// aelteren Laufs zuletzt ankommen und einen ueberholten Haushalt in den Store
// legen — den der naechste Lauf dann zurueckschrieb. Die Kette verwirft nie
// (jeder Lauf faengt seinen Fehler selbst), ein gescheiterter Lauf haelt die
// folgenden nicht auf. Bewusst ausserhalb des Stores, wie `foerderGeneration`.
let haushaltSpeicherKette: Promise<unknown> = Promise.resolve();
// Jeder Ladelauf zaehlt hoch; nur der juengste darf seine Antwort ablegen.
let haushaltLadeLauf = 0;

const initialState: CustomerState = {
  customerId: null,
  customer: null,
  partner: null,
  loading: false,
  error: null,
  insurances: null,
  funding: null,
  savings: { diagram: null, children: null },
  household: null,
  householdCustomerId: null,
  hiddenSubsidies: new Set(),
  isEditMode: false,
  selectedSubsidies: new Set(),
};

const useCustomerStore = create(
  persist<CustomerStore>(
    (set, state) => ({
      ...initialState,
      // `createCustomer` ist am 07.08.2026 entfallen (Entscheid Alex). Es war
      // die einzige FE-Anbindung an `POST /api/customers` und hatte nach dem
      // Wegfall von CreateCustomerDialog keinen Aufrufer mehr. Kunden entstehen
      // heute ausschliesslich ueber den Buchungs-/Wizard-Pfad
      // (`/api/book-meeting`, `/api/callcenter/save-customer`).
      fetchCustomer: async (customerKey, id) => {
        set({ loading: true });
        try {
          const customer = await api.customer.getCustomer(id.toString());
          set({ [customerKey]: customer });
          if (customerKey === 'customer') {
            set({ customerId: customer.id });
            if (customer.settings?.hiddenSubsidies) {
              set({
                hiddenSubsidies: new Set(customer.settings.hiddenSubsidies),
              });
            }
            state().getInsurances();
          }
        } catch (e) {
          set({ error: toErrorType(e) });
        } finally {
          set({ loading: false });
        }
      },
      updateCustomer: async (customerKey, id, data) => {
        const affectsFunding = Object.keys(data).some((key) =>
          FUNDING_RELEVANT_KEYS.includes(key),
        );

        set({ loading: true });
        try {
          const customer = await api.customer.updateCustomer(
            id.toString(),
            data,
          );
          set({ [customerKey]: customer });
          if (affectsFunding) {
            erneuereFoerderCache(set, state().getFunding);
          }
          if (customerKey === 'partner') {
            const customerId = state().customerId;
            if (customerId) {
              const refreshed = await api.customer.getCustomer(
                customerId.toString(),
              );
              set({ customer: refreshed });
            }
          }
        } catch (e) {
          set({ error: toErrorType(e) });
        } finally {
          set({ loading: false });
        }
      },
      // Per-Feld-Save für EditableField (Spiegel von caseDetail.updateCustomerField):
      // optimistic update + Rollback, wirft bei Fehler — updateCustomer schluckt
      // Fehler (set error) und würde im EditableField falschen Erfolg toasten.
      updateCustomerField: async (field, value) => {
        const customer = state().customer;
        if (!customer?.id) throw new Error('No customer');

        const oldValue = (customer as Record<string, unknown>)[field];
        set((s) => ({
          customer: s.customer ? { ...s.customer, [field]: value } : null,
        }));

        try {
          const updated = await api.customer.updateCustomer(String(customer.id), {
            [field]: value,
          });
          // 10aESTG ist server-berechnet (Estg10aService via CustomerResource),
          // kein editierbares Feld. Ohne Übernahme der PUT-Antwort bliebe der
          // §10a-EStG-Block nach dem Setzen der Trigger (employment_status,
          // use_riester, ...) veraltet (isEligible=false, alles 0) bis zum
          // nächsten Voll-Reload. Nur dieses abgeleitete Feld mergen — nicht das
          // ganze Objekt, sonst würde ein paralleler Feld-Edit kurz zurückgesetzt.
          if ('10aESTG' in updated) {
            set((s) => ({
              customer: s.customer
                ? { ...s.customer, '10aESTG': updated['10aESTG'] }
                : s.customer,
            }));
          }
          // Förder-relevante Felder invalidieren den funding-Cache — sonst
          // bleiben backend-berechnete Summen (Riester-sum_total, Kind-Zeilen)
          // bis zum nächsten Reload stale.
          if (FUNDING_RELEVANT_KEYS.includes(field)) {
            erneuereFoerderCache(set, state().getFunding);
          }
          return { oldValue, newValue: value };
        } catch (error) {
          set((s) => ({
            customer: s.customer ? { ...s.customer, [field]: oldValue } : null,
          }));
          throw feldSpeicherFehler(error, field);
        }
      },
      undoCustomerField: async (field, oldValue) => {
        const customer = state().customer;
        if (!customer?.id) return;
        set((s) => ({
          customer: s.customer ? { ...s.customer, [field]: oldValue } : null,
        }));
        const updated = await api.customer.updateCustomer(String(customer.id), {
          [field]: oldValue,
        });
        // Wie updateCustomerField: abgeleitetes 10aESTG aus der Antwort mergen.
        if ('10aESTG' in updated) {
          set((s) => ({
            customer: s.customer
              ? { ...s.customer, '10aESTG': updated['10aESTG'] }
              : s.customer,
          }));
        }
        // Spiegel von updateCustomerField: Undo eines förder-relevanten
        // Felds muss die Förder-Summen ebenfalls neu berechnen lassen.
        if (FUNDING_RELEVANT_KEYS.includes(field)) {
          set({ funding: null });
        }
      },
      clearState: () => {
        set(initialState);
      },
      getInsurances: async () => {
        const customerId = state().customer?.id;
        if (!customerId) return;
        set({ loading: true });
        try {
          const serverInsurances = await api.customer.getInsurances(
            customerId.toString(),
          );
          const current = (state().insurances || []) as LocalInsurance[];
          const localInsurances = current.filter((insurance) => !insurance.id);
          set({ insurances: [...serverInsurances, ...localInsurances] });
        } catch (e) {
          set({ error: toErrorType(e) });
        } finally {
          set({ loading: false });
        }
      },
      addInsurance: async (data: Partial<Insurance> & { __key?: string }) => {
        const customerId = state().customer?.id;
        if (!customerId) return;
        const { __key, ...payload } = data;
        try {
          await api.customer.addInsurance(customerId.toString(), payload);
          if (__key) {
            const current = state().insurances || [];
            const remaining = current.filter(
              (i) => (i as LocalInsurance).__key !== __key,
            );
            set({ insurances: remaining });
          }
          await state().getInsurances();
        } catch (e) {
          // rethrow to caller
          throw e;
        }
      },
      addEmptyLifeInsurance: async () => {
        const insurances = state().insurances || [];
        const uuid = `local-${generateUUID()}`;
        set({
          insurances: [
            ...insurances,
            {
              category: 'life_financial',
              __key: uuid,
            } as Insurance & { __key: string },
          ],
        });
      },
      deleteEmptyLifeInsurance: (keysToDelete) => {
        const insurances = (state().insurances || []) as LocalInsurance[];
        const filtered = insurances.filter((insurance) => {
          const key = insurance.id
            ? `id-${insurance.id}`
            : (insurance.__key ?? '');
          return !keysToDelete.includes(key);
        });
        set({ insurances: filtered });
      },
      updateInsurance: async (id, data: Partial<Insurance>) => {
        const customerId = state().customer?.id;
        if (!customerId) return;

        const currentInsurances = state().insurances || [];
        const updatedInsurances = currentInsurances.map((insurance) =>
          insurance.id === id ? { ...insurance, ...data } : insurance,
        );
        set({ insurances: updatedInsurances });

        set({ loading: true });
        try {
          await api.customer.updateInsurance(customerId.toString(), id, data);
        } catch (e) {
          await state().getInsurances();
          set({ error: toErrorType(e) });
        } finally {
          set({ loading: false });
        }
      },
      deleteInsurance: async (ids) => {
        const customerId = state().customer?.id;
        if (!customerId || !ids.length) return;
        set({ loading: true });
        try {
          await api.customer.deleteInsurance(customerId.toString(), ids);
          await state().getInsurances();
        } catch (e) {
          set({ error: toErrorType(e) });
        } finally {
          set({ loading: false });
        }
      },
      getFunding: async () => {
        const customerId = state().customerId;
        if (!customerId) return;

        // Mehrere Konzeptzeilen und die Fördertabelle fragen gleichzeitig an,
        // sobald der Cache leer ist — eine Abfrage je Stand genügt.
        const generation = foerderGeneration;
        if (
          foerderAbfrage &&
          foerderAbfrage.generation === generation &&
          foerderAbfrage.customerId === customerId
        ) {
          return foerderAbfrage.promise;
        }

        const abfrage = { generation, customerId, promise: Promise.resolve() };
        abfrage.promise = (async () => {
          set({ loading: true });
          try {
            const funding = await api.customer.getFundingsByCustomerId(
              customerId.toString(),
            );
            // Während der Abfrage wurde gespeichert (oder der Kunde gewechselt):
            // diese Antwort ist alt. Die neue Abfrage läuft schon.
            if (generation !== foerderGeneration) return;
            if (state().customerId !== customerId) return;
            set({ funding });
          } catch (e) {
            if (generation === foerderGeneration) {
              set({ error: toErrorType(e) });
            }
          } finally {
            set({ loading: false });
            if (foerderAbfrage === abfrage) foerderAbfrage = null;
          }
        })();
        foerderAbfrage = abfrage;
        return abfrage.promise;
      },
      calculateSaving: async (data, type: 'diagram' | 'children', signal) => {
        const customerId = state().customerId;
        if (!customerId) return;

        set({ loading: true });

        try {
          const savings = await api.customer.calculateSaving(
            customerId,
            data,
            type,
            signal,
          );
          if (signal?.aborted) return;
          set((s) => ({ savings: { ...s.savings, [type]: savings } }));
        } catch (e) {
          if (signal?.aborted) return;
          set({ error: toErrorType(e) });
        } finally {
          if (!signal?.aborted) set({ loading: false });
        }
      },
      // Die Kunden-ID kommt seit 19.10.2026 vom Aufrufer. Vorher las die
      // Funktion `customerId` aus dem Store — das `clearState()` beim
      // Kundenwechsel auf null setzt. Oeffnete die Reaktivierungs-Schranke
      // die Strecke, bevor der Kunde geladen war, lief der Aufruf ins Leere,
      // und das Formular stand mit Nullen da (Feedback 15.10.2026).
      getHousehold: async (kundeId) => {
        const customerId = kundeId ?? state().customerId;
        if (!customerId) return false;

        const lauf = ++haushaltLadeLauf;
        set({ loading: true });
        try {
          // Laufende Speicherlaeufe abwarten — sonst kann die Ladeantwort
          // einen Stand VOR dem letzten Speichern zeigen.
          await haushaltSpeicherKette;
          const antwort = await api.customer.getHousehold(
            customerId.toString(),
          );
          // Ueberholt (neuerer Ladelauf) oder der Kunde ist ein anderer:
          // diese Antwort gehoert nicht in den Store.
          const aktuellerKunde = state().customer?.id ?? state().customerId;
          if (lauf !== haushaltLadeLauf || aktuellerKunde !== customerId) {
            return false;
          }
          set({
            household: antwort?.data ?? null,
            householdCustomerId: customerId,
          });
          return true;
        } catch (e) {
          if (lauf === haushaltLadeLauf) set({ error: toErrorType(e) });
          return false;
        } finally {
          set({ loading: false });
        }
      },
      // Meldet, ob gespeichert wurde — vorher verschluckte ein leerer catch
      // jeden Fehlschlag ("update failed silently"), und der Berater sah eine
      // Haushaltsrechnung, die es serverseitig nie gab (FB Dobra 10.08.).
      //
      // Die Antwort wird ausserdem in den Bestand uebernommen: `household`
      // ist die Quelle, aus der die Geschwister-Abschnitte ihre selbst
      // angelegten Zeilen ableiten. Ohne diese Uebernahme blieben sie bis zum
      // naechsten getHousehold() auf einem veralteten Stand und schrieben ihn
      // beim naechsten Speichern zurueck.
      //
      // 🔥 Seit 19.10.2026 zwei Riegel (Feedback 15.10.2026):
      //  - Geschrieben wird nur auf einen Haushalt, der fuer GENAU diesen
      //    Kunden geladen wurde. Ein nie geladener Haushalt ist ein Formular
      //    voller Nullen — und das Backend ersetzt den Bestand komplett.
      //  - Speicherlaeufe laufen nacheinander (`haushaltSpeicherKette`), die
      //    Antworten kommen damit in Sendereihenfolge an.
      updateHousehold: (data: HouseholdUpdate): Promise<boolean> => {
        const customerId = state().customer?.id;
        if (!customerId || state().householdCustomerId !== customerId) {
          return Promise.resolve(false);
        }

        const lauf = haushaltSpeicherKette.then(async () => {
          try {
            const antwort = await api.customer.updateHousehold(
              customerId.toString(),
              data,
            );
            // Inzwischen ein anderer Kunde: die Antwort gehoert nicht mehr
            // in den Store.
            if (antwort?.data && state().householdCustomerId === customerId) {
              set({ household: antwort.data });
            }

            return true;
          } catch (e) {
            set({ error: toErrorType(e) });

            return false;
          }
        });
        haushaltSpeicherKette = lauf;

        return lauf;
      },
      getConceptProducts: async () => {
        const customerId = state().customer?.id || state().customerId;
        if (!customerId) return [];
        set({ loading: true });
        try {
          const products = await api.customer.getConceptProducts(
            customerId.toString(),
          );

          return products;
        } catch (e) {
          set({ error: toErrorType(e) });
          // error already set in state
          return [];
        } finally {
          set({ loading: false });
        }
      },

      addConceptProduct: async (productData) => {
        const customerId = state().customer?.id;
        if (!customerId) throw new Error('No customer ID');

        set({ loading: true });
        try {
          const created = await api.customer.addConceptProduct(
            customerId.toString(),
            productData,
          );
          return created;
        } catch (e) {
          set({ error: toErrorType(e) });
          throw e;
        } finally {
          set({ loading: false });
        }
      },

      updateConceptProduct: async (productId, productData) => {
        const customerId = state().customer?.id;
        if (!customerId) throw new Error('No customer ID');

        set({ loading: true });
        try {
          return await api.customer.updateConceptProduct(
            customerId.toString(),
            productId,
            productData,
          );
        } catch (e) {
          set({ error: toErrorType(e) });
          // Weiterwerfen: die Konzeptzeile zeigt den Fehler an. Verschluckt stand
          // die Zeile als gespeichert da und war beim nächsten Öffnen leer.
          throw e;
        } finally {
          set({ loading: false });
        }
      },

      deleteConceptProduct: async (productId) => {
        const customerId = state().customer?.id;
        if (!customerId) return;

        set({ loading: true });
        try {
          await api.customer.deleteConceptProduct(
            customerId.toString(),
            productId,
          );
        } catch (e) {
          set({ error: toErrorType(e) });
          throw e;
        } finally {
          set({ loading: false });
        }
      },
      getDocuments: async () => {
        const customerId = state().customerId;

        if (!customerId) return [];
        try {
          const result = await api.customer.getDocuments(customerId.toString());

          return result;
        } catch (e) {
          // rethrow to caller
          throw e;
        }
      },

      addDocument: async (doc: Omit<DocumentsTable, 'id' | 'comment'>) => {
        const customerId = state().customer?.id;
        if (!customerId) throw new Error('No customer ID');
        return await api.customer.addDocument(customerId.toString(), doc);
      },

      updateDocument: async (id: number, doc: Partial<DocumentsTable>) => {
        const customerId = state().customer?.id;
        if (!customerId) throw new Error('No customer ID');
        return await api.customer.updateDocument(
          customerId.toString(),
          id,
          doc,
        );
      },

      deleteDocument: async (id: number) => {
        const customerId = state().customer?.id;
        if (!customerId) throw new Error('No customer ID');
        return await api.customer.deleteDocument(customerId.toString(), id);
      },
      toggleEditMode: () => {
        set((state) => {
          if (!state.isEditMode) {
            return {
              isEditMode: true,
              selectedSubsidies: new Set(state.hiddenSubsidies),
            };
          } else {
            return {
              isEditMode: false,
              selectedSubsidies: new Set(),
            };
          }
        });
      },
      toggleSelectedSubsidy: (subsidyId) => {
        set((state) => {
          const newSelectedSubsidies = new Set(state.selectedSubsidies);
          const newHiddenSubsidies = new Set(state.hiddenSubsidies);

          if (newSelectedSubsidies.has(subsidyId)) {
            newSelectedSubsidies.delete(subsidyId);
          } else {
            newSelectedSubsidies.add(subsidyId);
          }

          if (newHiddenSubsidies.has(subsidyId)) {
            newHiddenSubsidies.delete(subsidyId);
          }

          return {
            selectedSubsidies: newSelectedSubsidies,
            hiddenSubsidies: newHiddenSubsidies,
          };
        });
      },
      hideSelectedSubsidies: async () => {
        const customerId = state().customer?.id;
        if (!customerId) return;

        const currentState = state();
        const newHiddenSubsidies = new Set(currentState.hiddenSubsidies);
        currentState.selectedSubsidies.forEach((id) => {
          newHiddenSubsidies.add(id);
        });

        const currentCustomer = currentState.customer;
        if (currentCustomer) {
          try {
            const updatedCustomer = await api.customer.updateCustomer(
              customerId.toString(),
              {
                settings: {
                  ...currentCustomer.settings,
                  hiddenSubsidies: Array.from(newHiddenSubsidies),
                },
              },
            );

            set({
              customer: updatedCustomer,
              hiddenSubsidies: newHiddenSubsidies,
              selectedSubsidies: new Set(),
              isEditMode: false,
            });
          } catch (e) {
            // save failed silently
          }
        }
      },
      showAllSubsidies: async () => {
        const customerId = state().customer?.id;
        if (!customerId) return;

        const currentCustomer = state().customer;
        if (currentCustomer) {
          try {
            const updatedCustomer = await api.customer.updateCustomer(
              customerId.toString(),
              {
                settings: {
                  ...currentCustomer.settings,
                  hiddenSubsidies: [],
                },
              },
            );

            set({
              customer: updatedCustomer,
              hiddenSubsidies: new Set(),
              selectedSubsidies: new Set(),
              isEditMode: false,
            });
          } catch (e) {
            // save failed silently
          }
        }
      },
      restoreHiddenSubsidies: () => {
        const currentCustomer = state().customer;
        const currentHiddenSubsidies = state().hiddenSubsidies;
        if (
          currentCustomer?.settings?.hiddenSubsidies &&
          currentHiddenSubsidies.size === 0
        ) {
          set({
            hiddenSubsidies: new Set(currentCustomer.settings.hiddenSubsidies),
          });
        }
      },
    }),
    {
      name: 'customer-storage',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => {
        return { customerId: state.customerId } as CustomerStore;
      },
    },
  ),
);

export default useCustomerStore;
